import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { AddItem } from '../../types/actions';
import type { CampaignData } from '../../../shared/types';
import TextAreaField from '../ui/TextAreaField';
import { api } from '../../api/client';
import { parseFormDraftResponse } from './aiFormDraftParser';
import AiDraftAssistPanel from './AiDraftAssistPanel';
import {
  getVisibilityAudiencePreview,
  getVisibilityOptions,
  normalizeVisibility,
} from '../permissions/visibilityOptions';

export default function SessionNoteForm({
  addItem,
  sessionNumber,
  updateCampaignInfo,
  canSetGmVisibility = false,
  submitRef,
  showSubmit = true,
}: {
  addItem: AddItem;
  sessionNumber: number;
  updateCampaignInfo: <K extends keyof CampaignData>(field: K, value: CampaignData[K]) => void;
  canSetGmVisibility?: boolean;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  showSubmit?: boolean;
}) {
  const [note, setNote] = useState({
    sessionNum: sessionNumber || 1,
    date: '',
    summary: '',
    highlights: '',
    visibility: 'public',
  });
  const [errors, setErrors] = useState<{ summary?: string }>({});
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState('');
  const [isDraftingWithAi, setIsDraftingWithAi] = useState(false);
  const visibilityOptions = getVisibilityOptions(canSetGmVisibility);

  const draftWithAi = useCallback(async () => {
    if (isDraftingWithAi) return;

    const prompt = aiPrompt.trim();
    const hasCurrentDraft = [note.summary, note.highlights].some((value) => value.trim().length > 0);
    if (!prompt && !hasCurrentDraft) {
      setAiError('Add a prompt or some draft text before generating.');
      return;
    }

    const systemPrompt = `You draft session notes for tabletop campaign logs.
Return ONLY JSON with keys: summary, highlights, date.
Rules:
- summary should be concise but complete.
- highlights should be short bullet-ready text.
- date, when provided, must be YYYY-MM-DD.
- do not include extra keys or markdown fences.`;
    const userPrompt = `Create or refine this session note draft.
Current draft:
- sessionNum: ${note.sessionNum}
- date: ${note.date || '(empty)'}
- summary: ${note.summary || '(empty)'}
- highlights: ${note.highlights || '(empty)'}
GM prompt: ${prompt || '(none)'}`;

    setIsDraftingWithAi(true);
    setAiError('');
    try {
      const result = await api.sendAssistantMessage(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        0.6
      );
      const parsed = parseFormDraftResponse(result.content, ['summary', 'highlights', 'date']);
      if (!parsed) {
        throw new Error('AI returned an unexpected format. Try a more specific prompt.');
      }

      setNote((previous) => ({
        ...previous,
        summary: parsed.summary ?? previous.summary,
        highlights: parsed.highlights ?? previous.highlights,
        date: parsed.date && /^\d{4}-\d{2}-\d{2}$/.test(parsed.date) ? parsed.date : previous.date,
      }));
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Failed to generate session draft with AI.');
    } finally {
      setIsDraftingWithAi(false);
    }
  }, [aiPrompt, isDraftingWithAi, note.date, note.highlights, note.sessionNum, note.summary]);

  const handleSubmit = useCallback(() => {
    if (!note.summary.trim()) {
      setErrors({ summary: 'Summary is required.' });
      return;
    }
    const normalizedSession = Math.max(1, Number.isFinite(note.sessionNum) ? note.sessionNum : sessionNumber || 1);
    addItem('sessionNotes', { ...note, sessionNum: normalizedSession });
    setNote({
      sessionNum: (sessionNumber || normalizedSession) + 1,
      date: '',
      summary: '',
      highlights: '',
      visibility: 'public',
    });
    setAiPrompt('');
    setAiError('');
    updateCampaignInfo('sessionNumber', (sessionNumber || normalizedSession) + 1);
    setErrors({});
  }, [addItem, note, sessionNumber, updateCampaignInfo]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
  }, [submitRef, handleSubmit]);

  return (
    <div className="bg-[var(--accent-muted)] p-4 rounded-lg border border-[var(--glass-border)] mb-4">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div className="field-block">
          <label className="field-label">Session #</label>
          <input
            type="number"
            placeholder="Session #"
            value={note.sessionNum}
            onChange={(e) =>
              setNote({
                ...note,
                sessionNum: Math.max(1, parseInt(e.target.value) || 1),
              })
            }
            className="input-field"
          />
        </div>
        <div className="field-block">
          <label className="field-label">Date</label>
          <input
            type="date"
            value={note.date}
            onChange={(e) => setNote({ ...note, date: e.target.value })}
            className="input-field"
          />
        </div>
      </div>
      <TextAreaField
        label="Session Summary"
        placeholder="Session Summary"
        value={note.summary}
        onChange={(value) => {
          setNote({ ...note, summary: value });
          if (errors.summary) setErrors({});
        }}
        hint="Key events, beats, and outcomes."
        error={errors.summary}
        showCount
        minRows={6}
      />
      <TextAreaField
        label="Key Highlights"
        placeholder="Key Highlights"
        value={note.highlights}
        onChange={(value) => setNote({ ...note, highlights: value })}
        hint="Moments worth revisiting later."
        showCount
        minRows={4}
      />
      <AiDraftAssistPanel
        value={aiPrompt}
        onChange={(value) => {
          setAiPrompt(value);
          if (aiError) setAiError('');
        }}
        onGenerate={() => void draftWithAi()}
        isGenerating={isDraftingWithAi}
        error={aiError}
        hint="Ask for a recap optimized for next-session prep and continuity."
      />
      <div className="field-block">
        <label className="field-label">Visibility</label>
        <select
          value={normalizeVisibility(note.visibility)}
          onChange={(e) => setNote({ ...note, visibility: e.target.value })}
          className="input-field"
        >
          {visibilityOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="field-meta">
          <span className="field-hint">
            {getVisibilityAudiencePreview(note.visibility)}
          </span>
        </div>
        {canSetGmVisibility && normalizeVisibility(note.visibility) === 'share_group' && (
          <div className="field-meta">
            <span className="field-hint">
              After saving, link a share group from the expanded session card to grant player access.
            </span>
          </div>
        )}
      </div>
      {showSubmit && (
        <button
          onClick={handleSubmit}
          className="btn-primary-solid flex items-center gap-2"
        >
          <Plus size={16} /> Add Session Note
        </button>
      )}
    </div>
  );
}
